import { forwardRef, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, LinearProgress, SpeedDial, SpeedDialAction, Typography } from "@mui/material";
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as WEBIFC from "web-ifc";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { rotateTo, zoomTo } from "./CameraControls";
import { fetchWithProgress, getFileSize } from "../../utils/file";
import TitleContainer from "../TitleContainer";

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "10px",
  overflow: "hidden",
  position: "relative",
};

const loadingContainerStyle = {
  height: "100%",
  width: "100%",
  top: 0,
  left: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  justifyContent: "center",
  alignItems: "center",
  background: "#c3c8cd",
};

const progressContainerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const COLORS = {
  inProgress: "#D6A659",
  delayed: "#D45B5B",
  hightlight: "#93FFF6",
  select: "#008ad8",
};

const IFCViewer = forwardRef(
  (
    {
      modelURL,
      backgroundModels,
      title,
      hideElementIds,
      delayedNotStartElementIds,
      inProgressElementIds,
      delayedElementIds,
      planCompleteElementIds,
      actualCompleteElementIds,
      showFilterElementIds,
      allElementIds,
      highlightElementIds,
      selectionHighlightElementIds,
      comments,
      onClick,
      selectComponents,
      onSelectionChange,
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasViewerInit, setHasViewerInit] = useState(false);
    const [progressData, setProgressData] = useState({ loaded: 0, total: 0 });
    const [expIDtoFragmentID, setExpIDtoFragmentID] = useState({});
    const [elementIDtoExpID, setElementIDtoExpID] = useState({});
    const [elementIDtoModel, setElementIDtoModel] = useState({});
    const [modeltoElementID, setModeltoElementID] = useState({});
    const [startAddingModels, setStartAddingModels] = useState(false);
    const [modelLoaded, setModelLoaded] = useState({});
    const [isDefaultSelected, setIsDefaultSelected] = useState(false);
    const container = useRef();
    const components = useRef();
    const world = useRef();
    const fragmentIfcLoader = useRef();
    const fragmentsManagers = useRef([]);
    const mainModels = useRef([]);
    const currentHover = useRef();
    const currentHighlight = useRef();
    const coloredElementIds = useRef();

    useEffect(() => {
      initViewer();
    }, []);

    useEffect(() => {
      // load main models
      if (modelURL && modelURL.length > 0 && ref.current && components.current && fragmentIfcLoader.current && hasViewerInit && !startAddingModels) {
        addModel();
      }
    }, [modelURL, ref.current, fragmentIfcLoader.current, hasViewerInit]);

    const addModel = async () => {
      setStartAddingModels(true);
      let anyNewModel = false;

      // Collect promises for file fetching (parallel)
      const fetchPromises = [];
      const urlList = [];

      for (let i = 0; i < modelURL.length; i++) {
        const url = modelURL[i].fileURL;
        if (!modelLoaded[url]) {
          console.log(`mainModelUrl ${i + 1}`, url);
          urlList.push(url);
          fetchPromises.push(
            fetchWithProgress(url, (p) => {
              setProgressData((prev) => {
                const urlProgress = {
                  ...prev.urlProgress,
                  [url]: { loaded: p.loaded, total: p.total },
                };
                const totalLoaded = Object.values(urlProgress).reduce((sum, progress) => sum + (progress.loaded || 0), 0);
                const totalSize = Object.values(urlProgress).reduce((sum, progress) => sum + (progress.total || 0), 0);
                return {
                  loaded: totalLoaded,
                  total: totalSize,
                  urlProgress,
                };
              });
            }).then((file) => file.arrayBuffer())
          );
          anyNewModel = true;
        } else {
          // if model has been loaded. change visibility based on background selection
          setModelLoaded((prev) => {
            prev[url].visible = modelURL[i].visible;
            return {
              ...prev,
            };
          });
        }
      }

      // Wait for all files to fetch in parallel, then load sequentially
      if (fetchPromises.length > 0) {
        setIsLoading(true);
        setProgressData({ loaded: 0, total: 0 });
        const buffers = await Promise.all(fetchPromises);

        // Load fragments sequentially to avoid webIfc concurrency issues
        for (let i = 0; i < urlList.length; i++) {
          await addMainIFC(urlList[i], buffers[i]);
        }

        setIsLoading(false);
      }

      setStartAddingModels(false);
      if (anyNewModel) {
        zoomTo(world.current.camera, mainModels.current);
      }
    };

    useEffect(() => {
      // load background models
      if (backgroundModels && backgroundModels.length > 0 && ref.current && components.current && fragmentIfcLoader.current && hasViewerInit) {
        backgroundModels.forEach((backgroundModel) => {
          if (!modelLoaded[backgroundModel.fileURL]) {
            if (backgroundModel.format === "ifc") {
              addBackgroundIFC(backgroundModel.fileURL, backgroundModel.visible);
            }
            if (backgroundModel.format === "fbx") {
              addBackgroundFBX(backgroundModel.fileURL, backgroundModel.visible);
            }
          } else {
            // if model has been loaded. change visibility based on background selection
            modelLoaded[backgroundModel.fileURL].visible = backgroundModel.visible;
          }
        });
      }
    }, [backgroundModels, ref.current, components.current, fragmentIfcLoader.current, hasViewerInit]);

    useEffect(() => {
      coloredElementIds.current = {
        delayedNotStartElementIds,
        inProgressElementIds,
        delayedElementIds,
      };
      const colorConfig = [
        { ids: inProgressElementIds, color: COLORS.inProgress },
        { ids: delayedElementIds, color: COLORS.delayed },
      ];

      if (allElementIds && ref.current?.classifier && Object.keys(expIDtoFragmentID).length && Object.keys(elementIDtoExpID).length) {
        // reset to original colors
        ref.current.classifier.resetColor(getFragmentIdMap(allElementIds));
        // reset outline
        ref.current.outlineDelayed?.clear("delayNotStarted");
        ref.current.outlineHighlighted?.clear("highlight");
        // Apply colors based on the configuration
        colorConfig.forEach(({ ids, color }) => {
          if (ids && ids.length) {
            colorElements(ids, color);
          }
        });
        if (delayedNotStartElementIds && ref.current?.outlineDelayed) {
          let visibleElements = Object.keys(modelLoaded)
            .map((key) => {
              if (modelLoaded[key].visible) {
                return modelLoaded[key].elementIDs;
              } else return [];
            })
            .flat();
          outlineDelayedElements(delayedNotStartElementIds.filter((id) => visibleElements.includes(id)));
        }

        // Apply visibility based on the configuration
        showElements(allElementIds); // reset visibility
        hideElements(hideElementIds); // hide target elements
      }
    }, [
      allElementIds,
      hideElementIds,
      delayedNotStartElementIds,
      inProgressElementIds,
      delayedElementIds,
      planCompleteElementIds,
      actualCompleteElementIds,
      ref.current?.classifier,
      ref.current?.outlineDelayed,
      expIDtoFragmentID,
      elementIDtoExpID,
      hasViewerInit,
      modelLoaded,
    ]);

    useEffect(() => {
      if (!ref.current || !ref.current.world) {
        console.warn("World is not initialized yet.");
        return;
      }
      if (
        highlightElementIds &&
        allElementIds &&
        ref.current?.outlineHighlighted &&
        Object.keys(expIDtoFragmentID).length &&
        Object.keys(elementIDtoExpID).length
      ) {
        outlineHighlightElements(highlightElementIds);
      }
    }, [highlightElementIds, ref.current?.world, ref.current?.outlineHighlighted, expIDtoFragmentID, elementIDtoExpID, hasViewerInit]);

    useEffect(() => {
      if (!ref.current || !ref.current.world) {
        console.warn("World is not initialized yet.");
        return;
      }
      if (
        selectionHighlightElementIds &&
        allElementIds &&
        ref.current?.selectionHighlight &&
        Object.keys(expIDtoFragmentID).length &&
        Object.keys(elementIDtoExpID).length
      ) {
        selectionHighlightElements(selectionHighlightElementIds);
      }
    }, [selectionHighlightElementIds, ref.current?.world, ref.current?.outlineHighlighted, expIDtoFragmentID, elementIDtoExpID, hasViewerInit]);

    useEffect(() => {
      if (comments && ref.current) {
        addCommentBubbles();
      }
    }, [comments, ref.current]);

    useEffect(() => {
      if (props.defaultSelected && ref.current?.selectionHighlight && !isDefaultSelected && onClick) {
        selectionHighlightElements([props.defaultSelected]);
        const expIDs = elementIDtoExpID[props.defaultSelected];
        const modelUrl = elementIDtoModel[props.defaultSelected];
        if (expIDs) {
          const fragmentIds = expIDs.map((id) => expIDtoFragmentID[modelUrl][id]).flat();
          const fragmentIdMap = getFragmentIdMap([props.defaultSelected]);
          fragmentIds?.forEach((fragmentId) => {
            fragmentsManagers.current.forEach((fragmentsManager) => {
              const fragment = fragmentsManager.list.get(fragmentId);
              const clonedFrag = fragment.clone(fragmentIdMap[fragmentId]);
              clonedFrag.mesh.position.set(0, 0, 0);
              clonedFrag.mesh.rotation.set(0, 0, 0);
              clonedFrag.mesh.applyMatrix4(fragment.mesh.matrixWorld);
              clonedFrag.mesh.computeBoundingBox();
              const center = new THREE.Vector3();
              center.copy(clonedFrag.mesh.boundingBox.getCenter(center));
              onClick(undefined, props.defaultSelected, center);
              setIsDefaultSelected(true);
            });
          });
        }
      }
    }, [modelLoaded, props.defaultSelected, ref.current?.selectionHighlight, fragmentsManagers.current]);

    const getFragmentIdMap = (elementIDs) => {
      const fragmentIdMap = {};
      elementIDs?.forEach((elementId) => {
        const modelUrl = elementIDtoModel[elementId];
        const expIDs = elementIDtoExpID[elementId];
        if (expIDs) {
          const fragmentIds = expIDs.map((id) => expIDtoFragmentID[modelUrl][id]).flat();
          fragmentIds?.forEach((id) => {
            if (!fragmentIdMap[id]) {
              fragmentIdMap[id] = new Set();
            }
            expIDs.forEach((expID) => {
              fragmentIdMap[id].add(expID);
            });
          });
        }
      });
      return fragmentIdMap;
    };

    const colorElements = (elementIDs, color) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.classifier.setColor(fragmentIdMap, new THREE.Color(color));
    };

    const outlineDelayedElements = (elementIDs) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.outlineDelayed.clear("delayNotStarted");
      ref.current.outlineDelayed.add("delayNotStarted", fragmentIdMap);
    };

    const outlineHighlightElements = (elementIDs) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.outlineHighlighted.clear("highlight");
      ref.current.outlineHighlighted.add("highlight", fragmentIdMap);
    };

    const selectionHighlightElements = (elementIDs) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.selectionHighlight.highlightByID("selectionHighlight", fragmentIdMap);
    };

    const showElements = (elementIDs) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.hider.set(true, fragmentIdMap);
    };

    const hideElements = (elementIDs) => {
      const fragmentIdMap = getFragmentIdMap(elementIDs);
      ref.current.hider.set(false, fragmentIdMap);
    };

    const initViewer = async () => {
      components.current = new OBC.Components();
      const worlds = components.current.get(OBC.Worlds);

      world.current = worlds.create();

      world.current.scene = new OBC.SimpleScene(components.current);
      world.current.renderer = new OBF.PostproductionRenderer(components.current, container.current, { logarithmicDepthBuffer: true });
      world.current.camera = new OBC.OrthoPerspectiveCamera(components.current);
      world.current.camera.three.far = 100000;
      world.current.camera.three.updateProjectionMatrix();

      components.current.init();

      world.current.renderer.postproduction.enabled = true;
      world.current.scene.setup();

      world.current.camera.updateAspect();

      world.current.scene.three.background = new THREE.Color("#c3c8cd");

      ref.current = { world: world.current };

      fragmentIfcLoader.current = components.current.get(OBC.IfcLoader);
      await fragmentIfcLoader.current.setup();
      fragmentIfcLoader.current.settings.webIfc.COORDINATE_TO_ORIGIN = false;

      const measurements = components.current.get(OBC.MeasurementUtils);
      const casters = components.current.get(OBC.Raycasters);
      const caster = casters.get(world.current);
      if (world.current.renderer && onClick) {
        const highlighter = components.current.get(OBF.Highlighter);
        ref.current.selectionHighlight = highlighter;
        if (world.current && world.current.renderer) {
          highlighter.setup({
            world: world.current,
            hoverEnabled: true,
            hoverColor: new THREE.Color(COLORS.select),
            selectName: "selectionHighlight",
            selectionColor: new THREE.Color(COLORS.select),
          });
          highlighter.events.hover.onHighlight.add((data) => {
            currentHover.current = data;
          });
          highlighter.events.hover.onClear.add(() => {
            let highLightElementIds = [];
            if (currentHover.current) {
              if (currentHighlight.current) {
                Object.keys(currentHighlight.current).forEach((key) => {
                  currentHighlight.current[key].forEach((expID) => {
                    const elementIds = Object.keys(elementIDtoExpID).filter((key) => elementIDtoExpID[key].includes(expID));
                    highLightElementIds.push(...elementIds);
                  });
                });
              }
              Object.keys(currentHover.current).forEach((key) => {
                currentHover.current[key].forEach((expID) => {
                  const elementIds = Object.keys(elementIDtoExpID).filter((key) => elementIDtoExpID[key].includes(expID));
                  setProgressColor(elementIds.filter((id) => !highLightElementIds.includes(id)));
                });
              });
              currentHover.current = undefined;
            }
          });
          highlighter.events.selectionHighlight.onHighlight.add((data) => {
            currentHighlight.current = data;
          });
          highlighter.events.selectionHighlight.onClear.add(() => {
            if (currentHighlight.current) {
              Object.keys(currentHighlight.current).forEach((key) => {
                currentHighlight.current[key].forEach((expID) => {
                  const elementIds = Object.keys(elementIDtoExpID).filter((key) => elementIDtoExpID[key].includes(expID));
                  setProgressColor(elementIds);
                });
              });
              currentHighlight.current = undefined;
            }
          });
        } else {
          console.error("The given world doesn't have a renderer!");
        }
        const canvas = world.current.renderer.three.domElement;
        canvas.addEventListener("click", () => {
          let visibleModels = Object.keys(modelLoaded).filter((url) => modelLoaded[url].visible);
          const result = caster.castRay(mainModels.current.filter((model) => visibleModels.includes(model.name)));

          if (!result) {
            onClick();
            return;
          }
          if (!(result.object instanceof THREE.Mesh)) {
            onClick(result.point);
            return;
          }
          if (result.faceIndex === undefined) {
            onClick(result.point);
            return;
          }
          const fragmentIdMap = {};
          fragmentIdMap[result.object.uuid] = new Set();
          fragmentIdMap[result.object.uuid].add(result.object.fragment.instanceToItem.get(result.instanceId));
          const bimElementIds = convertFragmentIdMapToElementIds(fragmentIdMap);

          const face = measurements.getFace(result.object, result.faceIndex, result.instanceId);

          if (face) {
            const edges = new THREE.EdgesGeometry();
            const points = [];
            for (const edge of face.edges) {
              points.push(...edge.points);
            }
            edges.setFromPoints(points);
            const lineMaterial = new THREE.LineBasicMaterial({
              color: 0xff0000,
              depthTest: false,
            });
            const line = new THREE.LineSegments(edges, lineMaterial);
            const boundingBox = new THREE.Box3().setFromObject(line);
            const center = new THREE.Vector3();
            center.copy(boundingBox.getCenter(center));
            onClick(result.point, bimElementIds[0], center);
          }
        });
      }

      if (!ref.current.classifier) {
        const classifier = components.current.get(OBC.Classifier);
        ref.current.classifier = classifier;
      }
      if (!ref.current.outlineDelayed && props.advanced) {
        const outlineDelayed = components.current.get(OBF.Outliner);
        ref.current.outlineDelayed = outlineDelayed;
        ref.current.outlineDelayed.world = world.current;
        ref.current.outlineDelayed.enabled = true;
        ref.current.outlineDelayed.create(
          "delayNotStarted",
          new THREE.MeshBasicMaterial({
            color: COLORS.delayed,
            transparent: true,
            opacity: 0.3,
          })
        );
      }
      if (!ref.current.outlineHighlighted && props.advanced) {
        const outlineHighlighted = components.current.get(OBF.Outliner);
        ref.current.outlineHighlighted = outlineHighlighted;
        ref.current.outlineHighlighted.world = world.current;
        ref.current.outlineHighlighted.enabled = true;
        ref.current.outlineHighlighted.create(
          "highlight",
          new THREE.MeshBasicMaterial({
            color: COLORS.hightlight,
            transparent: true,
            opacity: 0.3,
          })
        );
      }
      if (!ref.current.hider) {
        const hider = components.current.get(OBC.Hider);
        ref.current.hider = hider;
      }

      setHasViewerInit(true);
    };

    const convertFragmentIdMapToElementIds = (fragmentIdMap) => {
      const elementIds = new Set();
      Object.keys(fragmentIdMap).forEach((fragmentId) => {
        const expIDs = Array.from(fragmentIdMap[fragmentId]);
        expIDs.forEach((expressID) => {
          const entry = Object.entries(elementIDtoExpID).find(([key, value]) => value.includes(expressID));
          if (entry) {
            const [key, value] = entry;
            elementIds.add(key);
          }
        });
      });
      return Array.from(elementIds);
    };

    const addMainIFC = async (url, arrayBuffer) => {
      const buffer = new Uint8Array(arrayBuffer);

      // Create a new webIfc instance for each model to avoid concurrent access issues
      const webIfc = new WEBIFC.IfcAPI();
      webIfc.SetWasmPath("https://unpkg.com/web-ifc@0.0.59/", true);
      await webIfc.Init();

      // Open model with webIfc first to ensure it's initialized
      const modelID = webIfc.OpenModel(buffer);
      const exporter = components.current.get(OBC.IfcJsonExporter);
      const ifcData = await exporter.export(webIfc, modelID);

      // Now load with fragment loader sequentially
      const model = await fragmentIfcLoader.current.load(buffer);
      model.name = url;
      world.current.scene.three.add(model);
      const indexer = components.current.get(OBC.IfcRelationsIndexer);
      await indexer.process(model);
      expIDtoFragmentID[url] = {};
      model.items.forEach((item) => {
        const id = item.id;
        for (const exp_id of item.ids) {
          if (exp_id) {
            if (expIDtoFragmentID[url][exp_id]) {
              expIDtoFragmentID[url][exp_id].push(id);
            } else {
              expIDtoFragmentID[url][exp_id] = [id];
            }
          }
        }
      });
      let elementIDs = [];
      Object.keys(ifcData).forEach((key) => {
        if (ifcData[key].Tag) {
          if (elementIDtoExpID[ifcData[key].Tag.value]) {
            elementIDtoExpID[ifcData[key].Tag.value].push(ifcData[key].expressID);
          } else {
            elementIDtoExpID[ifcData[key].Tag.value] = [ifcData[key].expressID];
            elementIDtoModel[ifcData[key].Tag.value] = url;
          }
          if (!elementIDs.includes(ifcData[key].Tag.value)) {
            elementIDs.push(ifcData[key].Tag.value);
          }
        }
      });
      const fragmentsManager = components.current.get(OBC.FragmentsManager);
      fragmentsManager.load(buffer);
      fragmentsManagers.current.push(fragmentsManager);

      modelLoaded[url] = model;
      modelLoaded[url].elementIDs = elementIDs;
      mainModels.current.push(model);
      ref.current.modelLoaded = modelLoaded;
      ref.current.mainModels = mainModels.current;

      setExpIDtoFragmentID({ ...expIDtoFragmentID });
      setElementIDtoExpID({ ...elementIDtoExpID });
      setModelLoaded({ ...modelLoaded });
    };

    const addBackgroundIFC = async (url, visible) => {
      const file = await fetch(url);
      const data = await file.arrayBuffer();
      const buffer = new Uint8Array(data);
      const model = await fragmentIfcLoader.current.load(buffer);
      model.name = "example";
      model.visible = visible;
      world.current.scene.three.add(model);
      modelLoaded[url] = model;
      setModelLoaded({ ...modelLoaded });
    };

    const addBackgroundFBX = async (url, visible) => {
      const manager = new THREE.LoadingManager();
      const loader = new FBXLoader(manager);
      loader.load(url, function (fbx) {
        fbx.visible = visible;
        world.current.scene.three.add(fbx);
        modelLoaded[url] = fbx;
        setModelLoaded({ ...modelLoaded });
      });
    };

    const addCommentBubbles = () => {
      if (!ref.current.commentBubbles) {
        ref.current.commentBubbles = [];
      }
      const commentIds = comments.map((c) => c.commentId);
      ref.current.commentBubbles.forEach((cb) => {
        cb.visible = commentIds.includes(cb.commentId);
      });
      comments.forEach((comment, index) => {
        const target = ref.current.commentBubbles.filter((cb) => cb.commentId === comment.commentId);
        if (!target.length) {
          const geometry = new THREE.SphereGeometry(1, 32, 32);
          const material = new THREE.MeshBasicMaterial({
            color: comment.status === "Pending" ? "#f7e520" : "#C2C2C2",
          });
          const sphere = new THREE.Mesh(geometry, material);
          sphere.commentId = comment.commentId;
          sphere.position.set(comment.location.x, comment.location.y + 1, comment.location.z);
          world.current.scene.three.add(sphere);
          ref.current.commentBubbles.push(sphere);
        } else {
          target.material = new THREE.MeshBasicMaterial({
            color: comment.status === "Pending" ? "#f7e520" : "#C2C2C2",
          });
        }
      });
    };

    const setProgressColor = (elementIds) => {
      colorElements(
        elementIds.filter((id) => coloredElementIds.current.inProgressElementIds?.includes(id)),
        COLORS.inProgress
      );
      colorElements(
        elementIds.filter((id) => coloredElementIds.current.delayedElementIds?.includes(id)),
        COLORS.delayed
      );
    };

    return (
      <Box sx={containerStyle}>
        <Box sx={{ position: "absolute", left: "20px", top: "40px", zIndex: 99 }}>
          {selectComponents && (
            <>
              <IconButton
                sx={{ p: 0, width: 40, height: 40 }}
                onClick={() => {
                  if (props.homeViewOnClick) {
                    props.homeViewOnClick();
                  } else {
                    if (ref.current) {
                      zoomTo(ref.current.world.camera, mainModels.current);
                    }
                  }
                }}
              >
                <img src={"assets/buttons/home.svg"} alt={"home"}></img>
              </IconButton>
              <SpeedDial
                ariaLabel="SpeedDial basic example"
                direction="right"
                sx={{ width: 40, height: 40, position: "absolute", left: 70, top: 0 }}
                icon={<img src={"assets/buttons/kompass.svg"} alt={"kompass"}></img>}
              >
                {["Top", "Bottom", "Front", "Back", "Left", "Right"].map((side) => (
                  <SpeedDialAction
                    key={side}
                    icon={<Typography sx={{ fontSize: "0.6rem" }}>{side}</Typography>}
                    FabProps={{
                      sx: {
                        borderRadius: "5px",
                        margin: "4px 0px 4px 8px",
                      },
                    }}
                    onClick={() => {
                      if (props.rotateToViewOnClick) {
                        props.rotateToViewOnClick(side);
                      } else {
                        if (ref.current) {
                          rotateTo(ref.current.world.camera, side);
                        }
                      }
                    }}
                  />
                ))}
              </SpeedDial>
            </>
          )}
          {selectComponents?.map((selectComponent, index) => (
            <selectComponent.Component
              key={index}
              options={selectComponent.options}
              value={selectComponent.value}
              isMultiSelect={selectComponent.isMultiSelect}
              required={selectComponent.required}
              onChange={(value) => {
                selectComponent.onChange(value);
                onSelectionChange && onSelectionChange(value, selectComponent.selectLabel);
              }}
              selectLabel={selectComponent.selectLabel}
              placeholder={selectComponent.placeholder}
            />
          ))}
        </Box>
        {title && (
          <TitleContainer
            sx={{
              position: "absolute",
              zIndex: 1,
            }}
            title={title}
            icon={"assets/icons/model.svg"}
            buttonIcon={props.buttonIcon}
            onClick={props.buttonOnClick}
          ></TitleContainer>
        )}
        {isLoading && (
          <Box sx={loadingContainerStyle}>
            <Box sx={{ width: 200 }}>
              <LinearProgress
                variant={progressData.loaded < progressData.total ? "determinate" : "indeterminate"}
                value={progressData.total > 0 ? (progressData.loaded / progressData.total) * 100 : 0}
              />
            </Box>
            <Box sx={progressContainerStyle}>
              <Typography variant="caption">
                {progressData.loaded < progressData.total
                  ? `Downloading model ${getFileSize(progressData.loaded)} / ${getFileSize(progressData.total)}`
                  : `Loading model...`}
              </Typography>
            </Box>
          </Box>
        )}
        <Box ref={container} width={"100%"} height={"100%"}></Box>
      </Box>
    );
  }
);

export default IFCViewer;
